import React, { FC } from 'react';
import AppToggle from '@/components/atoms/AppToggle';
import { Text, Caption } from '@/components';

export interface ConfigToggleProps {
  label: string;
  checked: boolean;
  onChange: (checked: boolean) => void;
  description?: string;
  icon?: React.ReactNode;
  /** Tailwind gradient classes for the checked card bg */
  toggleCheckedBg?: string;
  /** Tailwind border class when checked */
  toggleCheckedBorder?: string;
  /** Tailwind text-color class for the label when checked */
  toggleCheckedText?: string;
  /** Tailwind bg gradient for the icon container when checked */
  toggleCheckedIconBg?: string;
  /** Accent colour applied to the toggle thumb */
  switchColor?: 'blue' | 'purple' | 'green' | 'red' | 'orange' | 'teal';
  /** Alias for switchColor — accepted from section files */
  customColor?: 'blue' | 'purple' | 'green' | 'red' | 'orange' | 'teal';
  /** Extra classes on the outer wrapper */
  className?: string;
  /** data-testid forwarded to the outer wrapper */
  testId?: string;
  /** Disables interaction and dims the card */
  disabled?: boolean;
}

const SWITCH_COLORS: Record<
  NonNullable<ConfigToggleProps['switchColor']>,
  string
> = {
  blue: 'bg-blue-500',
  purple: 'bg-purple-500',
  green: 'bg-green-500',
  red: 'bg-red-500',
  orange: 'bg-orange-500',
  teal: 'bg-teal-500',
};

const ConfigToggle: FC<ConfigToggleProps> = ({
  label,
  checked,
  onChange,
  description,
  icon,
  toggleCheckedBg = 'from-orange-50 via-red-50 to-purple-50 dark:from-orange-900/20 dark:via-red-900/20 dark:to-purple-900/20',
  toggleCheckedBorder = 'border-primary dark:border-orange-400',
  toggleCheckedText = 'text-primary dark:text-orange-400',
  toggleCheckedIconBg = 'bg-gradient-to-br from-primary to-secondary',
  switchColor,
  customColor,
  className = '',
  testId,
  disabled = false,
}) => {
  const resolvedSwitchColor = customColor ?? switchColor;
  const thumbColor = resolvedSwitchColor
    ? SWITCH_COLORS[resolvedSwitchColor]
    : undefined;

  // Always show the actual saved value; interaction is blocked via overlay + disabled prop
  const effectiveChecked = checked;

  return (
    <div className={`group relative ${className}`} data-testid={testId ?? ''}>
      {/* Ambient glow */}
      <div
        className={`absolute inset-0 bg-gradient-to-r from-primary/10 to-secondary/10 rounded-2xl blur-xl transition-opacity duration-300 ${effectiveChecked ? 'opacity-50' : 'opacity-0 group-hover:opacity-100'}`}
      />

      <div
        className={`relative rounded-2xl border-2 transition-all duration-300 shadow-sm hover:shadow-lg ${effectiveChecked
          ? `bg-gradient-to-r ${toggleCheckedBg} ${toggleCheckedBorder} scale-[1.02]`
          : 'bg-gradient-to-r from-gray-50 to-gray-100 dark:from-gray-800 dark:to-gray-700 border-gray-300 dark:border-gray-600'
          }`}
      >
        <div className="flex items-center justify-between gap-3 p-4 sm:p-5">
          {/* Left: icon + text */}
          <div className="flex items-center gap-3 flex-1 min-w-0">
            {icon && (
              <div
                className={`flex items-center justify-center w-10 h-10 rounded-xl transition-all duration-300 ${effectiveChecked
                  ? `${toggleCheckedIconBg} shadow-lg scale-110`
                  : 'bg-gray-200 dark:bg-gray-600 group-hover:bg-gray-300 dark:group-hover:bg-gray-500'
                  }`}
              >
                <div
                  className={`transition-colors duration-300 ${effectiveChecked ? 'text-white' : 'text-gray-600 dark:text-gray-300'}`}
                >
                  {icon}
                </div>
              </div>
            )}
            <div className="flex flex-col">
              <Text
                as="span"
                size="base"
                weight="bold"
                color="default"
                leading="tight"
                className={`transition-colors duration-300 cursor-pointer ${effectiveChecked ? toggleCheckedText : 'text-gray-700 dark:text-gray-300'}`}
              >
                {label}
              </Text>
              {description && (
                <Caption
                  size="xs"
                  color="muted"
                  leading="none"
                  className="mt-0.5"
                >
                  {description}
                </Caption>
              )}
            </div>
          </div>

          {/* Toggle switch */}
          <div className={`flex-shrink-0${disabled ? ' relative opacity-50' : ''}`}>
            {disabled && <div className="absolute inset-0 z-10 cursor-not-allowed" />}
            <AppToggle
              checked={effectiveChecked}
              onChange={onChange}
              color={resolvedSwitchColor as any}
              size="sm"
              testId={testId}
              disabled={disabled}
            />
          </div>
        </div>
      </div>
    </div>
  );
};

export default ConfigToggle;
